import {
  type User,
  WorkflowStepStatuses,
  type WorkflowStepInput,
  type WorkflowStepUser,
  WorkflowStepUserResponses,
  type WorkflowTemplate,
} from "~/api/types";
import useUsers from "~/api/swr/useUsers";
import { UserStatus, PlatformRole } from "~/config/types";

type TemplateStepAssignments = WorkflowTemplate["steps"][number]["users"];

function resolveStepUsers(
  assignments: TemplateStepAssignments,
  assignableUsers: User[],
): WorkflowStepUser[] {
  return assignments.flatMap((assignment) => {
    const user = assignableUsers.find(({ id }) => id === assignment.userId);

    if (!user) return [];

    return [
      {
        userId: assignment.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        imageUrl: user.imageUrl,
        permission: assignment.permission,
        responseStatus: WorkflowStepUserResponses.pending,
      },
    ];
  });
}

export function useWorkflowUsers() {
  const { data: allUsers } = useUsers({
    status: [UserStatus.active, UserStatus.pending],
    roles: [
      PlatformRole.superAdmin,
      PlatformRole.coordinator,
      PlatformRole.contributor,
    ],
  });

  const hydrateSteps = (
    template: WorkflowTemplate,
  ): WorkflowStepInput[] | null => {
    if (!allUsers) return null;

    return template.steps.map((step, i) => ({
      _key: `${step.id ?? i}`,
      id: step.id,
      name: step.name,
      description: step.description ?? "",
      type: step.type,
      order: i,
      status: WorkflowStepStatuses.upcoming,
      dueAt: step.dueAt ? new Date(step.dueAt) : null,
      users: resolveStepUsers(step.users, allUsers),
    }));
  };

  return { hydrateSteps };
}
